import { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Header from "./components/Header/Header";

const ROOT_PATH = "https://hambler-api.onbeta.in/v1/";
const WEB_IMAGE_BASE_URL = "https://www.hambler.in/";
const BANNERS_API_URL = `${ROOT_PATH}product/getbanners`;
const FEATURED_CATEGORIES_API_URL = `${ROOT_PATH}product/getfeaturedcategories`;
const FEATURED_PARENT_ID = 21;
const PRODUCT_CARD_ACCENT_CLASSES = [
  "",
  "bg-light-blue",
  "bg-light-yellow",
  "bg-light-pink",
];

const buildAssetUrl = (assetPath = "") => {
  const cleanedPath = `${assetPath}`.trim();

  if (!cleanedPath) {
    return "";
  }

  return cleanedPath.startsWith("http")
    ? cleanedPath
    : `${WEB_IMAGE_BASE_URL}${cleanedPath.replace(/^\/+/, "")}`;
};

const buildResponsiveImageSet = (imageSet = "") =>
  `${imageSet}`
    .split(",")
    .map((entry) => {
      const trimmedEntry = entry.trim();

      if (!trimmedEntry) {
        return "";
      }

      const parts = trimmedEntry.split(/\s+/);
      const descriptor = /^\d+w$/i.test(parts[parts.length - 1]) ? parts.pop() : "";
      const imagePath = parts.join(" ");

      return [buildAssetUrl(imagePath), descriptor].filter(Boolean).join(" ");
    })
    .filter(Boolean)
    .join(", ");

const decodeHtmlToText = (value = "") => {
  const normalizedValue = `${value}`.trim();

  if (!normalizedValue) {
    return "";
  }

  if (typeof document === "undefined") {
    return normalizedValue
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">")
      .replace(/&amp;/gi, "&")
      .replace(/&nbsp;/gi, " ")
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  const textarea = document.createElement("textarea");
  textarea.innerHTML = normalizedValue;

  const parsedDocument = new DOMParser().parseFromString(textarea.value, "text/html");

  return parsedDocument.body.textContent?.replace(/\s+/g, " ").trim() || "";
};

const buildCategoryUrl = (slug = "") => {
  const cleanedSlug = `${slug}`.trim();

  if (!cleanedSlug) {
    return "#";
  }

  return cleanedSlug.startsWith("http")
    ? cleanedSlug
    : `/products/${cleanedSlug.replace(/^\/+/, "")}`;
};

const formatProductCount = (count = 0) => {
  const numericCount = Number(count) || 0;

  if (!numericCount) {
    return "Explore category";
  }

  return `${numericCount} ${numericCount === 1 ? "Product" : "Products"}`;
};

const normalizeFeaturedCategories = (items = []) => {
  const uniqueCategories = [];
  const seenCategoryKeys = new Set();

  items.forEach((item, index) => {
    if (!item?.IsActive) {
      return;
    }

    const categoryKey = `${Number(item?.Parent) || 0}-${
      item?.ID ?? item?.CatUrl ?? item?.Name ?? index
    }`;

    if (seenCategoryKeys.has(categoryKey)) {
      return;
    }

    seenCategoryKeys.add(categoryKey);

    uniqueCategories.push({
      id: item?.ID ?? categoryKey,
      name: item?.Name?.trim() || "Product",
      slug: item?.CatUrl || "",
      description: decodeHtmlToText(item?.MetaDescription || item?.Description),
      image: buildAssetUrl(item?.ImagePath),
      imageSet: buildResponsiveImageSet(item?.ImageSet),
      parentId: Number(item?.Parent) || 0,
      totalProductsCount: Number(item?.TotalProductsCount) || 0,
    });
  });

  return uniqueCategories;
};

const buildCategorySections = (items = []) => {
  const normalizedCategories = normalizeFeaturedCategories(items);
  const featuredProducts = normalizedCategories.filter(
    (category) => category.parentId === FEATURED_PARENT_ID
  );
  const otherProducts = normalizedCategories.filter(
    (category) => category.parentId !== FEATURED_PARENT_ID
  );
  const sections = [];

  if (featuredProducts.length) {
    sections.push({
      id: "featured-products",
      title: "Featured Products",
      subtitle: "hawa bhi jyada, bachat bhi jyada",
      items: featuredProducts,
    });
  }

  if (otherProducts.length) {
    sections.push({
      id: "other-products",
      title: "Other Products",
      subtitle: "Explore more categories across Hambler",
      items: otherProducts,
    });
  }

  return sections;
};

function Home() {
  const [banners, setBanners] = useState([]);
  const [productSections, setProductSections] = useState([]);
  const [isBannerLoading, setIsBannerLoading] = useState(true);
  const [isSectionLoading, setIsSectionLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchBanners = async () => {
      try {
        const response = await fetch(BANNERS_API_URL, { method: "GET" });
        const data = await response.json();

        if (!isMounted) {
          return;
        }

        if (!data?.Status || !Array.isArray(data?.Result)) {
          setBanners([]);
          return;
        }

        const activeBanners = data.Result.filter((banner) => banner?.IsActive).map(
          (banner) => ({
            id: banner.ID,
            title: banner.Title || "Banner",
            image: buildAssetUrl(banner.ImgUrl),
            mobileImage: banner.MobileImgUrl
              ? buildAssetUrl(banner.MobileImgUrl)
              : banner.MobileImageUrl
              ? buildAssetUrl(banner.MobileImageUrl)
              : "",
            redirectUrl: banner.RedirectUrl || "",
          })
        );

        setBanners(activeBanners);
      } catch (error) {
        console.error("Failed to fetch banners:", error);

        if (isMounted) {
          setBanners([]);
        }
      } finally {
        if (isMounted) {
          setIsBannerLoading(false);
        }
      }
    };

    const fetchFeaturedCategories = async () => {
      try {
        const response = await fetch(FEATURED_CATEGORIES_API_URL, { method: "GET" });
        const data = await response.json();

        if (!isMounted) {
          return;
        }

        if (!data?.Status || !Array.isArray(data?.Result)) {
          setProductSections([]);
          return;
        }

        setProductSections(buildCategorySections(data.Result));
      } catch (error) {
        console.error("Failed to fetch featured categories:", error);

        if (isMounted) {
          setProductSections([]);
        }
      } finally {
        if (isMounted) {
          setIsSectionLoading(false);
        }
      }
    };

    fetchBanners();
    fetchFeaturedCategories();

    return () => {
      isMounted = false;
    };
  }, []);

  const renderBannerImage = (banner) => (
    <picture>
      {banner.mobileImage && (
        <source media="(max-width: 991px)" srcSet={banner.mobileImage} />
      )}
      <img src={banner.image} alt={banner.title} />
    </picture>
  );

  return (
    <>
      <Header />

      <div className="container-wrapper">
        <div className="main-slider">
          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            slidesPerView={1}
            speed={1000}
            spaceBetween={0}
            loop
            autoplay={{ delay: 2500 }}
            pagination={{ clickable: true }}
            navigation
            className="swiper"
          >
            {isBannerLoading && (
              <SwiperSlide>
                <div className="slide-item">
                  <img src="assets/images/silder/slider1.jpg" alt="Loading banner" />
                </div>
              </SwiperSlide>
            )}

            {banners.map((banner) => (
              <SwiperSlide key={banner.id}>
                <div className="slide-item">
                  {banner.redirectUrl ? (
                    <a href={banner.redirectUrl} target="_blank" rel="noreferrer">
                      {renderBannerImage(banner)}
                    </a>
                  ) : (
                    renderBannerImage(banner)
                  )}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>

      <div className="featured-sec ptb50">
        <Container>
          {isSectionLoading && (
            <div className="featured-status">Loading featured categories...</div>
          )}

          {!isSectionLoading && !productSections.length && (
            <div className="featured-status">
              No featured categories are available right now.
            </div>
          )}

          {productSections.map((section) => (
            <div key={section.id} className="featured-section-block">
              <div className="section-title">
                <h3>{section.title}</h3>
                <div className="section-subtitle">{section.subtitle}</div>
              </div>

              <Row>
                {section.items.map((category, index) => (
                  <Col key={`${section.id}-${category.id}`} xl="4" md="6" sm="6" xs="12">
                    <div
                      className={`product-card product-card-2 ${
                        PRODUCT_CARD_ACCENT_CLASSES[index % PRODUCT_CARD_ACCENT_CLASSES.length]
                      }`}
                    >
                      <div className="product-img">
                        <img
                          src={category.image}
                          srcSet={category.imageSet || undefined}
                          className="img-fluid"
                          alt={category.name}
                          loading="lazy"
                        />
                      </div>

                      <div className="product-text">
                        <span>{formatProductCount(category.totalProductsCount)}</span>
                        <h4>{category.name}</h4>
                        <p>{category.description}</p>
                        <a href={buildCategoryUrl(category.slug)} className="btn btn-rounded">
                          <span className="d-md-block">
                            Explore Now
                            <i className="fa-light fa-arrow-right-long ms-2" />
                          </span>
                        </a>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          ))}
        </Container>
      </div>

      <section className="why-sec bg-black">
        <Container>
          <Row>
            <Col lg="5" md="12">
              <h3>Why Choose Hambler?</h3>
              <img src="assets/images/new-img/why-fan.png" alt="Why Choose Hambler?" className="why-fan-img img-fluid" />
            </Col>
            <Col lg="7" md="12">
              <p>
                Hambler is a trusted name in innovation, quality, and energy efficiency.
                Designed with cutting-edge BLDC and induction motor technology, our
                products deliver powerful performance while saving up to 65%
                electricity. Whether it is ceiling fans, pedestal fans, geysers, or
                home appliances, Hambler ensures durability, silent operation, and
                modern style that perfectly fits every space.
              </p>
              <p>
                We focus on creating eco-friendly products that combine comfort with
                sustainability. Every Hambler product undergoes strict quality checks
                to guarantee long-lasting performance and user safety. With sleek
                designs, advanced features, and reliable after-sales service, Hambler
                stands for more than just cooling or heating. It represents smart
                living.
              </p>
              <p>
                By choosing Hambler, you are not only choosing comfort but also
                contributing to a greener planet. Our mission is to redefine everyday
                home experiences with technology that saves energy, enhances
                convenience, and lasts longer. Hambler powers comfort, style, and
                sustainability for every home.
              </p>
              <div className="icon-row">
                <div className="icon-box">
                  <img
                    src="./images/new-img/why-icon-1.png"
                    alt="2-WAY ROTATION FOR SUMMER AND WINTER"
                  />
                  <p>2-WAY ROTATION FOR SUMMER AND WINTER</p>
                </div>
                <div className="icon-box">
                  <img
                    src="./images/new-img/why-icon-2.png"
                    alt="POINT ANYWHERE RF REMOTE"
                  />
                  <p>POINT ANYWHERE RF REMOTE</p>
                </div>
                <div className="icon-box">
                  <img src="./images/new-img/why-icon-3.png" alt="TURBO MODE" />
                  <p>TURBO MODE</p>
                </div>
                <div className="icon-box">
                  <img
                    src="./images/new-img/why-icon-4.png"
                    alt="BUILT IN TIMER FOR 2, 4 AND 8 HOUR"
                  />
                  <p>BUILT IN TIMER FOR 2, 4 AND 8 HOUR</p>
                </div>
                <div className="icon-box">
                  <img src="./images/new-img/why-icon-5.png" alt="100% COPPER WINDING" />
                  <p>100% COPPER WINDING</p>
                </div>
                <div className="icon-box">
                  <img src="./images/new-img/why-icon-6.png" alt="6 SPEED AIR FLOW" />
                  <p>6 SPEED AIR FLOW</p>
                </div>
                <div className="icon-box">
                  <img src="./images/new-img/why-icon-7.png" alt="UNIQUE LED DISPLAY" />
                  <p>UNIQUE LED DISPLAY</p>
                </div>
                <div className="icon-box">
                  <img
                    src="/Assets/HomePage/images/new-img/why-icon-8.png"
                    alt="UP TO 65% ENERGY SAVING WITH BLDC MOTOR"
                  />
                  <p>UP TO 65% ENERGY SAVING WITH BLDC MOTOR</p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
}

export default Home;
